import {
  Component,
  ElementRef,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { Field } from '../../model/formBuilderInterface';
import { ADDRESS_FORM_CONTROL_NAME } from '../../constants/constant';
import { CommonService } from '@shared/services/common.service';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '@environments/environment';
import { ADDRESS_DISPLAY_CONST, Fields } from '@constants/constant';
import { Application } from '@enums/application.enums';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  getFirstFormattedAddressSegment,
  getFormatedAddressFromAddressComponents,
  getStreetLineFromAddressComponents
} from '@utils/common.utils';
import { HeaderFooterStateService } from '@shared/services/header-footer-state.service';

@Component({
  selector: 'app-location-input',
  templateUrl: './location-input.component.html',
  styleUrls: ['./location-input.component.scss']
})
export class LocationInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() form: FormGroup;
  @Input() field: Field;
  @ViewChild('locationFieldIframe') locationFieldIframe?: ElementRef<HTMLIFrameElement>;
  private locationIframeWindow: Window | null = null;
  options = {
    types: ['geocode'],
    strictBounds: true
  };
  mapUrl: any;
  customIframeHeight = true;
  iframeHeight = '55px';
  private messageEventListener!: (event: MessageEvent) => void;
  private destroy$ = new Subject<void>();

  constructor(
    public cs: CommonService,
    private sanitizer: DomSanitizer,
    private headerFooterStateService: HeaderFooterStateService
  ) {}

  onLocationIframeLoad(): void {
    this.locationIframeWindow =
      this.locationFieldIframe?.nativeElement?.contentWindow ?? null;
  }

  private isMessageFromThisIframe(event: MessageEvent): boolean {
    return (
      this.locationIframeWindow != null &&
      event.source === this.locationIframeWindow
    );
  }

  ngOnInit() {
    this.messageEventListener = (event: MessageEvent) => {
      if (event.origin + '/' === environment.EMBED_BASE_URL) {
        if (event.data?.type === Fields.ADDRESS_DROPDOWN_CLOSED) {
          if (this.isMessageFromThisIframe(event)) {
            this.customIframeHeight = true;
          }
        } else if (event.data?.type === Fields.ADDRESS_DROPDOWN_OPENED) {
          if (this.isMessageFromThisIframe(event)) {
            this.customIframeHeight = false;
            this.iframeHeight = event.data?.totalHeight + Application.TEN + 'px';
          }
        } else if (event.data?.type === Fields.ADDRESS_INPUT_SELECTED) {
          /** Only this row’s iframe runs logic (venue vs street are isolated). */
          const fromThisIframe = this.isMessageFromThisIframe(event);
          const address = getFormatedAddressFromAddressComponents(
            event.data?.address_components
          );
          const placeFormatted =
            typeof event.data?.place?.formatted_address === 'string'
              ? event.data.place.formatted_address.trim()
              : '';
          const lat = event.data?.lat;
          const long = event.data?.long;

          if (fromThisIframe) {
            const addressStr = typeof address === 'string' ? address.trim() : '';
            const streetLine = getStreetLineFromAddressComponents(
              event.data?.address_components
            );
            const fullFormatted = addressStr || placeFormatted;
            const preferStreetLine =
              !!this.field.addressUseStreetLineOnly ||
              this.field.changeValueFieldName === 'street_address';
            const streetOnlyValue = preferStreetLine
              ? streetLine || getFirstFormattedAddressSegment(placeFormatted)
              : '';
            const secondaryFormatted = preferStreetLine
              ? streetOnlyValue
              : fullFormatted;
            const hasLinkedField = !!this.field?.changeValueFieldName;

            if (!hasLinkedField) {
              if (this.field?.addressUseStreetLineOnly && streetOnlyValue) {
                this.form.controls[this.field.name]?.setValue(streetOnlyValue, {
                  emitEvent: false
                });
              } else if (this.field?.venueSearchEnabled && placeFormatted) {
                this.form.controls[this.field.name]?.setValue(placeFormatted, {
                  emitEvent: false
                });
              } else if (addressStr) {
                this.form.controls[this.field.name]?.setValue(addressStr, {
                  emitEvent: false
                });
              }
            } else if (event.data) {
              const primaryDisplay = this.getPrimaryDisplayWhenLinked(
                event.data as Record<string, unknown>,
                secondaryFormatted
              );
              if (primaryDisplay) {
                this.form.controls[this.field.name]?.setValue(primaryDisplay, {
                  emitEvent: false
                });
              }
            }

            if (hasLinkedField && secondaryFormatted) {
              this.form.controls[this.field.changeValueFieldName]?.setValue(
                secondaryFormatted,
                { emitEvent: true }
              );
            }
            this.updateMapUrl();

            const components = event.data?.address_components;
            if (
              Array.isArray(components) &&
              this.field.onGooglePlaceAddressComponents
            ) {
              this.field.onGooglePlaceAddressComponents(
                this.form,
                event.data,
                this.field.name
              );
            }
          }
          if (!isNaN(Number(lat))) {
            this.form.controls[ADDRESS_FORM_CONTROL_NAME.LATITUDE]?.setValue(
              lat,
              { emitEvent: false }
            );
          }

          if (!isNaN(Number(long))) {
            this.form.controls[ADDRESS_FORM_CONTROL_NAME.LONGITUDE]?.setValue(
              long,
              { emitEvent: false }
            );
          }
        }
        else if (event.data?.type === Fields.ADDRESS_INPUT_CHANGE) {
          if (this.isMessageFromThisIframe(event)) {
            this.iframeHeight = event.data?.totalHeight + 65 + 'px';
          }
        }
      }
    };
    window.addEventListener('message', this.messageEventListener);

    if (this.form && this.field) {
      this.form
        .get(this.field.name)
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe(() => this.updateMapUrl());
    }

    if (this.cs.isBrowser) {
      this.headerFooterStateService.themeChangeSubject
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.updateMapUrl();
        });
    }
  }

  ngOnChanges() {
    if (this.field && this.form) {
      this.updateMapUrl();
    }
  }

  ngOnDestroy() {
    if (this.cs.isBrowser) {
      window.removeEventListener('message', this.messageEventListener);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getPrimaryDisplayWhenLinked(
    data: Record<string, unknown>,
    secondaryFormatted: string
  ): string {
    const pick = (v: unknown): string | null =>
      typeof v === 'string' && v.trim() ? v.trim() : null;

    const secondary = (secondaryFormatted || '').trim();
    const differsFromSecondary = (s: string) => !secondary || s !== secondary;

    const place = data['place'] as Record<string, unknown> | undefined;
    const structured = data['structured_formatting'] as Record<string, unknown> | undefined;

    const fromPayload =
      pick(data['name']) ??
      pick(place?.['name']) ??
      pick(structured?.['main_text']);

    if (fromPayload && differsFromSecondary(fromPayload)) {
      return fromPayload;
    }

    const components = data['address_components'] as
      | Array<{ long_name?: string; types?: string[] }>
      | undefined;
    if (Array.isArray(components)) {
      for (const t of [ADDRESS_DISPLAY_CONST.establishment, ADDRESS_DISPLAY_CONST.point_of_interest, ADDRESS_DISPLAY_CONST.premise]) {
        const c = components.find((x) => x.types?.includes(t));
        const ln = pick(c?.long_name);
        if (ln && differsFromSecondary(ln)) {
          return ln;
        }
      }
    }

    const typed =
      pick(data['input']) ??
      pick(data['query']) ??
      pick(data['searchText']) ??
      pick(data['location']) ??
      pick(data['value']);
    if (typed) {
      return typed;
    }

    if (secondary.includes(',')) {
      const head = secondary.split(',')[Application.ZERO].trim();
      if (head && differsFromSecondary(head)) {
        return head;
      }
    }

    return secondary || fromPayload || '';
  }

  private updateMapUrl(): void {
    let theme = localStorage.getItem('theme') || Application.THEME_DARK;
    let whiteLabelTheme;
    if (this.cs.isWhiteLabelWeb) {
      const root = document.documentElement;
      const computedStyles = getComputedStyle(root);
      const configSecondaryBgColor = computedStyles.getPropertyValue('--configSecondaryBgColor').trim();
      const configPostTextColor = computedStyles.getPropertyValue('--configPostTextColor').trim();
      const configBgColor = computedStyles.getPropertyValue('--configBgColor').trim();
      const configBgTextColor = computedStyles.getPropertyValue('--configBgTextColor').trim();
      const configThemeColor = computedStyles.getPropertyValue('--configThemeColor').trim();
      whiteLabelTheme = {
        "configSecondaryBgColor": configSecondaryBgColor,
        "configPostTextColor": configPostTextColor,
        "configBgColor": configBgColor,
        "configBgTextColor": configBgTextColor,
        "configThemeColor": configThemeColor
      }
      whiteLabelTheme = JSON.stringify(whiteLabelTheme)
      whiteLabelTheme = encodeURIComponent(whiteLabelTheme)
      theme = 'theme-whiteLabel';
    }

    const websiteUrl = environment.websiteURL;
    const venueSearchParam = this.field.venueSearchEnabled ? '&venueSearchEnabled=true' : '';
    const primary = this.form.get(this.field.name)?.value;
    const locationQuery =
      typeof primary === 'string' ? primary : primary != null ? String(primary) : '';
    const url = `${environment.EMBED_BASE_URL}location?theme=${theme}&location=${encodeURIComponent(locationQuery)}&parentUrl=${websiteUrl}&whiteLabelThemeData=${whiteLabelTheme}&maxHeight=200px${venueSearchParam}`;
    this.mapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
